import React, { Fragment, useCallback, useRef, useContext } from 'react';
import animateScrollTo from 'animated-scroll-to';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import './new-event-style.css';

import ErrorList from 'components/Form/ErrorList';
import Wizard from 'components/Wizard/Wizard';
import WizardStep from 'components/Wizard/WizardStep';
import TailwindcssBlock from 'components/Tailwindcss/Block';

import NewEventStep1, { validate as validateStep1 } from './NewEvent.Step1';
import NewEventStep2, { validate as validateStep2 } from './NewEvent.Step2';
import NewEventStep3, { validate as validateStep3 } from './NewEvent.Step3';
import { createTournament } from 'api/events';
import useNetworkState from 'hooks/useNetworkState';
import { NotificationAlertsContext } from 'components/NotificationAlerts';
import { ALERT_TYPES } from 'components/NotificationAlerts/NotificationAlerts';

const labels = {
  country_id: 'País',
  event_type: 'Tipo de evento',
  registration_type: 'Tipo de suscripción',
  format: 'Formato del torneo',
  sport_id: 'Deporte',
  start_date: 'Fecha de inicio',
  last_registration_date: 'Último dia de inscripción',
  last_lineup_registration_at: 'Fecha límite para inscribir jugadores',
  event_time: 'Hora del evento',
  place: 'Nombre del lugar',
  coordinates: 'Coordenadas',
  registration_cost: 'Costo de inscripción',
  minimum_members: 'Mínimo de integrantes',
  avatar: 'Cover / Imagen del evento',
  name: 'Nombre',
  description: 'Descripción',
  best_of: 'Gana juego el mejor de',
};

const buildTournament = ({ coordinates, best_of, registration_cost, ...values }) => {
  return {
    ...values,
    registration_cost: Number(registration_cost),
    event_config: {
      best_of: Number(best_of),
    },
    lat: coordinates?.lat,
    lng: coordinates?.lng,
  };
};

const NewEvent = ({ history }) => {
  const titleRef = useRef(null);
  const { isLoading, setIsLoading, setDoneLoading, setFailedLoading, onDoneBy } = useNetworkState();
  const { createAlert } = useContext(NotificationAlertsContext);

  const onSubmit = useCallback(
    async values => {
      setIsLoading();

      try {
        const { body, status } = await createTournament(buildTournament(values));

        setDoneLoading();

        onDoneBy(status, {
          201: () => {
            history.push(`/dashboard/v2/evento/${body?.data?.tournament?.id}`);
            window.scrollTo(0, 0);
            createAlert('Se creó el evento correctamente.', ALERT_TYPES.INFO, 10000);
          },
          400: () => {
            createAlert(
              'Uno o varios campos no fueron enviados correctamente. Porfavor verifica que la información enviada es correcta e intenta nuevamente.',
              ALERT_TYPES.ERROR,
            );
          },
        });
      } catch (_) {
        console.log(_);

        setFailedLoading();
      }

      return Promise.resolve({});
    },
    [createAlert, history, onDoneBy, setDoneLoading, setFailedLoading, setIsLoading],
  );

  const afterNavigate = useCallback(() => {
    if (titleRef && titleRef.current) {
      animateScrollTo(titleRef.current);
    }
  }, []);

  return (
    <div className="mb-2 max-w-xl mx-auto">
      <h1 className="text-gray-600 text-3xl font-400 mb-6" ref={titleRef}>
        Evento Nuevo
      </h1>
      <div className="m-auto bg-white p-6 rounded-lg">
        <Wizard
          onSubmit={onSubmit}
          afterNavigate={afterNavigate}
          validateOnBlur={false}
          renderFooter={({ onNext, onPrev, hasPrev, isLast, formProps }) => (
            <Fragment>
              {!isEmpty(formProps?.errors) && formProps?.touched && (
                <ErrorList errors={formProps?.errors} labels={labels} />
              )}
              <div className="flex justify-between">
                <button
                  onClick={onPrev}
                  disabled={!hasPrev}
                  className={classnames('p-2 max-w-sm rounded-lg block mt-8 text-center', {
                    'bg-turquoise text-brandblue': hasPrev,
                    'bg-gray-400 text-gray-600': !hasPrev,
                  })}
                >
                  Anterior
                </button>

                <TailwindcssBlock
                  bg-turquoise={!isLoading}
                  bg-gray-200={isLoading}
                  text-brandblue={!isLoading}
                  text-gray-600={isLoading}
                  p-2
                  max-w-sm
                  rounded-lg
                  block
                  mt-8
                  text-center
                >
                  <button onClick={onNext}>
                    <Fragment>
                      {isLoading && 'Espera...'}
                      {!isLoading && <Fragment>{isLast ? 'Finalizar' : 'Siguiente'}</Fragment>}
                    </Fragment>
                  </button>
                </TailwindcssBlock>
              </div>
            </Fragment>
          )}
        >
          <WizardStep validate={validateStep1}>
            <NewEventStep1 />
          </WizardStep>
          <WizardStep validate={validateStep2}>
            <NewEventStep2 />
          </WizardStep>
          <WizardStep validate={validateStep3}>
            <NewEventStep3 />
          </WizardStep>
        </Wizard>
      </div>
    </div>
  );
};

export default NewEvent;
